import React, { useContext, useEffect } from 'react';
import { useDrop } from 'react-dnd';
import 'react-pdf/dist/esm/Page/PageCanvas';
import { useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import { Document, Page, pdfjs } from 'react-pdf/dist/esm/entry.vite';

import Sidebar from '../Sidebar/Sidebar';
import { LineItemsModal } from '../LineItems';
import DragabbleChips from '../DraggableChips';
import { CustomDragLayer } from '../CustomDragLayer';
import ControlToolbar from '../Header/ControlToolbar';
import { SignatureOrderModal } from '../SignatureOrder';
import DeletePageConfirmationModal from './Modal/DeletePageConfirmation';
import { DeleteIcon, FontPlusIcon } from '../DraggableChips/optionsIcons';
import TextBoxPreviewOverlayChip from '../PDFOverlayChips/TextBox/Preview';
import DropdownPreviewOverlayChip from '../PDFOverlayChips/Dropdown/Preview';
import CheckboxPreviewOverlayChip from '../PDFOverlayChips/Checkbox/Preview';
import SignatureAndInitialsPreviewChip from '../PDFOverlayChips/SignatureAndInitials/Preview';
import ExpandOnHoverButton from '../CustomButton/ExpandOnHoverButton';

import './css.css';
import APP_CONFIG from '../../config';
import { useGenerate } from '../../hooks/useGenerate';
import { FileContext } from '../../context/FileContext';
import { getDropdownLabels } from './useGetDropdownLabels';

import {
  AddRemovePageButton,
  DocumentOverlay,
  PDFPageOverlay,
  RelativeContainer,
  ScaledContainer,
} from './PreviewPdfForGenerate.styled';
import { PDFDocument } from 'pdf-lib';
import CkEditorConfirmationModal from './Modal/CkEditorConfirmationModal';
import { useNavigate } from 'react-router-dom';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

const PreviewPdfForGenerate = ({ isPreview }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const {
    itemId,
    fileId,
    numPages,
    pageScale,
    documentRef,
    previewFile,
    setPageScale,
    initialFields,
    pdfDimensions,
    hasPageLoaded,
    addPlaceHolder,
    addedFormFields,
    pageDeleteIndex,
    pagesAddedByUser,
    updateFormFields,
    showLineItemModal,
    setPageDeleteIndex,
    handleRenderSuccess,
    currentSelectedItem,
    onDocumentLoadSuccess,
    showSignatureOrderModal,
    pdfIndividualPageDetails,
    setShowSignateOrderModal,
    showCKEditorEntryConfirmation,
    setShowCKEditorEntryConfirmation,
    removePlaceholder
  } = useContext(FileContext);

  const { activeFile, activeFileType, addPageInPdfFileData } = useGenerate();

  const selectedItem = addedFormFields[currentSelectedItem.current];

  const scalingFactor = Number(pageScale.value);
  const overlayScalingFactor =
    pdfDimensions.scale < Number(pageScale.value)
      ? pdfDimensions.scale
      : Number(pageScale.value);
  useEffect(() => {
    async function getPDFScale(file) {
      const pdfDoc = await PDFDocument.load(file);
      const pages = pdfDoc.getPages();
      const largestWidth = Math.max(
        ...pages.map((x) => {
          const dimension = x.getSize();
          const angle = x.getRotation().angle;
          let updatedWidth = dimension.width;
          if (angle === 90 || angle === 270) {
            updatedWidth = dimension.height;
          }
          return updatedWidth;
        }),
      );
      const deviceWidth = window.innerWidth * 0.8; // width of container where pdf is loaded
      const calculatedScale = deviceWidth / largestWidth;
      const formattedScale = Math.floor(calculatedScale * 10) / 10;
      if (formattedScale < 1.5) {
        const scale = {
          value: formattedScale.toString(),
          label: `${formattedScale * 100}%`,
        };
        setPageScale(scale);
      }
    }
    if (activeFile) {
      getPDFScale(activeFile);
    }
  }, [activeFile]);

  const queryData = queryClient.getQueryData(['get-columns', itemId, fileId]);

  // initial drop
  const [, drop] = useDrop({
    accept: 'Column',
    drop: (item, monitor) => {
      const offset = monitor.getSourceClientOffset();
      if (offset && documentRef.current) {
        const rect = documentRef.current.getBoundingClientRect();
        //NOTE: here rect top is header and control toolbar section and 16 is the initial gap after controltoolbar to start of pdf
        addPlaceHolder({
          item,
          x: offset.x - rect.left - 16, //margin on every pages
          y: offset.y - rect.top - 16 + documentRef.current?.scrollTop, //margin on every pages
        });
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  // while dragging chips
  const [, dropChips] = useDrop({
    accept: 'Chips',
    drop: (item, monitor) => {
      const offset = monitor.getSourceClientOffset();
      if (offset && documentRef.current) {
        const rect = documentRef.current.getBoundingClientRect();
        //NOTE: here rect top is header and control toolbar section and 16 is the initial gap after controltoolbar to start of pdf
        addPlaceHolder({
          item,
          x: offset.x - rect.left - 16, //margin on every pages
          y: offset.y - rect.top - 16 + documentRef.current?.scrollTop, //margin on every pages
          update: true,
        });
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  const attachRef = (el) => {
    drop(el);
    dropChips(el);
  };

  const handleItemClick = (index) => {
    addedFormFields.forEach((item) => (item.isActive = false));
    if (index > -1) {
      addedFormFields[index] = {
        ...addedFormFields[index],
        isActive: !addedFormFields[index]?.isActive,
      };
      currentSelectedItem.current = index;
    }
    updateFormFields([...addedFormFields]);
  };

  const getPDFDocumentContainerHeight = () => {
    const styles = { height: '100%' };
    if (!isPreview && currentSelectedItem.current !== null) {
      if (selectedItem?.itemId === 'checkbox') {
        styles.height = 'calc(100% - 38px)';
      } else {
        styles.height = 'calc(100% - 56px)';
      }
    }
    return styles;
  };

  function sumHeightsAboveIndex(heights, pageIndex) {
    return heights?.reduce((acc, item, index) => {
      if (index < pageIndex) {
        return acc + item.height;
      }
      return acc;
    }, 0);
  }

  const extractIdAndCoordinates = (fields) => {
    return fields.map((field) => ({
      id: field.id,
      placeholder: field.placeholder,
    }));
  };

  function openCKEditor() {
    const clonedAddedFields = extractIdAndCoordinates(addedFormFields);
    const clonedInitialFields = extractIdAndCoordinates(
      initialFields?.find((x) => x.fileId === fileId)?.fields || [],
    );
    if (
      addedFormFields.length &&
      JSON.stringify(clonedAddedFields) !== JSON.stringify(clonedInitialFields)
    ) {
      setShowCKEditorEntryConfirmation(true);
    } else {
      const path = `/${fileId}/${itemId}/word-editor`;
      navigate(path);
    }
  }

  return (
    <div className="pdf__viewer">
      {!isPreview ? <Sidebar isGeneratePDFPage /> : null}
      <RelativeContainer isPreview={isPreview}>
        {showLineItemModal && (
          <LineItemsModal fileId={fileId} itemId={itemId} />
        )}
        {showSignatureOrderModal && (
          <SignatureOrderModal closeModal={setShowSignateOrderModal} />
        )}
        {/* change */}
        {!!pageDeleteIndex && (
          <DeletePageConfirmationModal isFromGenerateFlow />
        )}
        {!!showCKEditorEntryConfirmation && (
          <CkEditorConfirmationModal type="generate" />
        )}
        <div className="preview_portion">
          <ControlToolbar isPreview={isPreview} isGeneratePDFPage />
          <div
            className="preview-pdf_document__container"
            ref={attachRef}
            style={getPDFDocumentContainerHeight()}
          >
            {/* {!isPreview && ['docx', 'doc'].includes(activeFileType) ? (
              <div className="word_editor_trigger_container">
                <ExpandOnHoverButton handleClick={openCKEditor} />
              </div>
            ) : null} */}
            <ScaledContainer
              ref={documentRef}
              className="pdf__document position-relative"
            >
              {/* pdf rendering in the background */}
              <Document
                file={isPreview ? previewFile : activeFile}
                onLoadSuccess={onDocumentLoadSuccess}
                loading={
                  <div style={{ color: 'black', border: '1px solid red' }}>
                    {t('Loading')}
                  </div>
                }
                onLoadError={(err) => console.log(err)}
                renderMode="canvas"
                externalLinkTarget="_blank"
                externalLinkRel="_blank"
              >
                {Array.from(new Array(numPages), (el, pageIndex) => (
                  <Page
                    onRenderSuccess={handleRenderSuccess}
                    key={`page_${pageIndex + 1}`}
                    pageNumber={pageIndex + 1}
                    loading={<div>{t('Loading')}</div>}
                    renderTextLayer={false}
                    renderAnnotationLayer={true}
                    externalLinkTarget="_blank"
                    scale={scalingFactor}
                  ></Page>
                ))}
              </Document>

              {/* absolute class overlay for all the draggable components */}
              <DocumentOverlay scalingFactor={overlayScalingFactor}>
                <CustomDragLayer />
                {Array.from(new Array(numPages), (el, pageIndex) => (
                  <PDFPageOverlay
                    key={pageIndex}
                    height={pdfIndividualPageDetails?.[pageIndex]?.height}
                    width={pdfIndividualPageDetails?.[pageIndex]?.width}
                    page={pageIndex}
                    pageAboveHeight={
                      pageIndex > 0
                        ? sumHeightsAboveIndex(
                            pdfIndividualPageDetails,
                            pageIndex,
                          )
                        : 0
                    }
                    scale={overlayScalingFactor}
                  >
                    <>
                      <AddRemovePageButton
                        position="bottom"
                        color="green"
                        onClick={() => addPageInPdfFileData(pageIndex + 1)}
                      >
                        <FontPlusIcon color="green" />
                        Add New Page
                      </AddRemovePageButton>

                      {pageIndex > 0 && pagesAddedByUser.includes(pageIndex) ? (
                        <AddRemovePageButton
                          position="top"
                          color="#d83a52"
                          onClick={() => setPageDeleteIndex(pageIndex)}
                        >
                          <DeleteIcon color="#d83a52" />
                          Delete This Page
                        </AddRemovePageButton>
                      ) : null}
                    </>

                    <div style={{ position: 'absolute', top: 0, left: 0 }}>
                      {!isPreview &&
                        hasPageLoaded &&
                        addedFormFields.map(
                          (item, index) =>
                            item.formField.pageNumber === pageIndex + 1 && (
                              <DragabbleChips
                                item={item}
                                onItemClick={() => handleItemClick(index)}
                                key={item.id}
                                x={item?.placeholder?.x}
                                y={item?.placeholder?.y}
                                documentRef={documentRef}
                                handleRemovePlaceholder={removePlaceholder}
                              />
                            ),
                        )}

                      {isPreview &&
                        addedFormFields.map(
                          (item, index) =>
                            Object.keys(APP_CONFIG.form_fields.standard_fields)
                              .map(
                                (obj) =>
                                  APP_CONFIG.form_fields.standard_fields[obj]
                                    .id,
                              )
                              .includes(item.itemId) &&
                            item.formField.pageNumber === pageIndex + 1 &&
                            (item.itemId ===
                            APP_CONFIG.form_fields.standard_fields.checkbox
                              .id ? (
                              <CheckboxPreviewOverlayChip
                                item={item}
                                key={item.id}
                                x={item?.placeholder?.x}
                                y={item?.placeholder?.y}
                                showColor
                              />
                            ) : item.itemId ===
                              APP_CONFIG.form_fields.standard_fields.textBox
                                .id ? (
                              <TextBoxPreviewOverlayChip
                                item={item}
                                key={item.id}
                                x={item?.placeholder?.x}
                                y={item?.placeholder?.y}
                                showColor
                              />
                            ) : item.itemId ===
                              APP_CONFIG.form_fields.standard_fields.dropdown
                                .id ? (
                              <DropdownPreviewOverlayChip
                                item={item}
                                key={item.id}
                                x={item?.placeholder?.x}
                                y={item?.placeholder?.y}
                                options={getDropdownLabels({
                                  boardColumns:
                                    queryData?.data?.items?.[0]?.board
                                      ?.columns || [],
                                  targetColumnId: item?.column?.value || null,
                                })}
                                showColor
                              />
                            ) : (
                              <SignatureAndInitialsPreviewChip
                                item={item}
                                key={item.id}
                                x={item?.placeholder?.x}
                                y={item?.placeholder?.y}
                                showColor
                              />
                            )),
                        )}
                    </div>
                  </PDFPageOverlay>
                ))}
              </DocumentOverlay>
            </ScaledContainer>
          </div>
        </div>
      </RelativeContainer>
    </div>
  );
};

export default PreviewPdfForGenerate;
